// src/components/ConfiguracoesPeople.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Link } from 'react-router-dom'; // <-- Adicionado Link
import {
    FaPlus, FaTrash, FaPen, FaSave, FaPalette, FaQuestionCircle, FaGripVertical, FaFileAlt, FaArrowUp, FaArrowDown, FaEnvelope // <-- Adicionado FaEnvelope
} from 'react-icons/fa';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ConfiguracoesPeople.css';

// Componente para uma pergunta individual arrastável
const SortableQuestion = ({ question, onEdit, onDelete, pageIndex }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className="question-item" {...attributes}>
             <div className="drag-handle" {...listeners} title="Reordenar pergunta">
                <FaGripVertical />
            </div>
            <span className="question-title">{question.label} <strong>({question.type})</strong></span>
            <div className="question-actions">
                <button onClick={() => onEdit(pageIndex, question.id)} className="action-btn edit-btn" title="Editar"><FaPen /></button>
                <button onClick={() => onDelete(pageIndex, question.id)} className="action-btn delete-btn" title="Excluir"><FaTrash /></button>
            </div>
        </div>
    );
};

const ConfiguracoesPeople = ({ userData, onSettingsUpdate }) => {
    const [activeTab, setActiveTab] = useState('questions');
    const [pages, setPages] = useState([]);
    const [visuals, setVisuals] = useState({ primaryColor: '#002454', secondaryColor: '#8dbefd', logoUrl: '' });
    const [newLogoFile, setNewLogoFile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [editingInfo, setEditingInfo] = useState({ pageIndex: null, questionId: null });

    const empresaId = userData?.empresa_id;

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const loadSettings = useCallback(async () => {
        if (!empresaId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const settingsDocRef = doc(db, 'Empresas', empresaId, 'settings', 'onboarding');
            const settingsDoc = await getDoc(settingsDocRef);
            if (settingsDoc.exists()) {
                const data = settingsDoc.data();
                if (data.pages && data.pages.length > 0) {
                     setPages(data.pages.map(p => ({ ...p, id: p.id || crypto.randomUUID() })));
                } else if (data.questions && data.questions.length > 0) {
                    setPages([{ id: crypto.randomUUID(), title: 'Informações Iniciais', questions: data.questions }]);
                } else {
                    setPages([]);
                }
                setVisuals(data.visuals || { primaryColor: '#002454', secondaryColor: '#8dbefd', logoUrl: '' });
            }
        } catch (error) {
            console.error("Erro ao carregar configurações:", error);
            alert("Não foi possível carregar as configurações.");
        } finally {
            setIsLoading(false);
        }
    }, [empresaId]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const addPage = () => {
        const newPage = { id: crypto.randomUUID(), title: `Nova Página ${pages.length + 1}`, questions: [] };
        setPages(prev => [...prev, newPage]);
    };

    const deletePage = (pageIndex) => {
        if (window.confirm(`Tem certeza que deseja excluir a página "${pages[pageIndex].title}" e todas as suas perguntas?`)) {
            setPages(prev => prev.filter((_, index) => index !== pageIndex));
        }
    };

    const updatePageTitle = (pageIndex, newTitle) => {
        setPages(prev => prev.map((page, index) => index === pageIndex ? { ...page, title: newTitle } : page));
    };

    const movePage = (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= pages.length) return;
        setPages(items => arrayMove(items, fromIndex, toIndex));
    };

    const openModalForNew = (pageIndex) => {
        setCurrentQuestion({ id: crypto.randomUUID(), label: '', type: 'text', required: true, options: '' });
        setEditingInfo({ pageIndex, questionId: null });
        setIsModalOpen(true);
    };

    const openModalForEdit = (pageIndex, questionId) => {
        const questionToEdit = pages[pageIndex].questions.find(q => q.id === questionId);
        if (questionToEdit) {
            setCurrentQuestion({ ...questionToEdit });
            setEditingInfo({ pageIndex, questionId });
            setIsModalOpen(true);
        }
    };

    const handleQuestionChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentQuestion(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSaveQuestion = () => {
        if (!currentQuestion.label || !currentQuestion.type) {
            alert("Por favor, preencha o nome e o tipo da pergunta.");
            return;
        }
        const { pageIndex, questionId } = editingInfo;
        setPages(prevPages => {
            const newPages = [...prevPages];
            if (questionId) {
                const questions = newPages[pageIndex].questions.map(q => q.id === questionId ? currentQuestion : q);
                newPages[pageIndex] = { ...newPages[pageIndex], questions };
            } else {
                const questions = [...newPages[pageIndex].questions, currentQuestion];
                newPages[pageIndex] = { ...newPages[pageIndex], questions };
            }
            return newPages;
        });
        setIsModalOpen(false);
        setCurrentQuestion(null);
        setEditingInfo({ pageIndex: null, questionId: null });
    };

    const handleDeleteQuestion = (pageIndex, questionId) => {
        if (window.confirm("Tem certeza que deseja excluir esta pergunta?")) {
            setPages(prevPages => {
                const newPages = [...prevPages];
                const questions = newPages[pageIndex].questions.filter(q => q.id !== questionId);
                newPages[pageIndex] = { ...newPages[pageIndex], questions };
                return newPages;
            });
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const activePageIndex = pages.findIndex(p => p.questions.some(q => q.id === active.id));
            const overPageIndex = pages.findIndex(p => p.questions.some(q => q.id === over.id));
            
            if (activePageIndex !== -1 && activePageIndex === overPageIndex) {
                 setPages((prevPages) => {
                    const newPages = [...prevPages];
                    const activePage = newPages[activePageIndex];
                    const oldIndex = activePage.questions.findIndex(q => q.id === active.id);
                    const newIndex = activePage.questions.findIndex(q => q.id === over.id);
                    activePage.questions = arrayMove(activePage.questions, oldIndex, newIndex);
                    return newPages;
                });
            }
        }
    };

    const handleVisualChange = (e) => setVisuals(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleLogoChange = (e) => {
        if (e.target.files[0]) {
            setNewLogoFile(e.target.files[0]);
        }
    };

    const handleSaveSettings = async () => {
        if (!empresaId) return alert("ID da empresa não encontrado.");
        setIsSaving(true);
        try {
            const settingsDocRef = doc(db, 'Empresas', empresaId, 'settings', 'onboarding');
            let logoUrl = visuals.logoUrl;

            if (newLogoFile) {
                const logoPath = `empresas/${empresaId}/logo/${newLogoFile.name}`;
                const storageRef = ref(storage, logoPath);
                await uploadBytes(storageRef, newLogoFile);
                logoUrl = await getDownloadURL(storageRef);
            }

            const updatedVisuals = { ...visuals, logoUrl };
            
            await setDoc(settingsDocRef, {
                pages: pages,
                visuals: updatedVisuals,
                lastUpdated: new Date()
            }, { merge: true });

            setVisuals(updatedVisuals);
            setNewLogoFile(null);
            if (onSettingsUpdate) onSettingsUpdate(updatedVisuals);
            alert("Configurações salvas com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar configurações:", error);
            alert(`Falha ao salvar as configurações: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <div>Carregando configurações...</div>;

    return (
        <div className="config-people-container">
            <header className="config-header">
                <h1>Configurações da Empresa</h1>
                <p>Personalize a experiência de onboarding, notificações e a identidade visual da sua empresa.</p>
            </header>
            <div className="config-tabs">
                <button onClick={() => setActiveTab('questions')} className={activeTab === 'questions' ? 'active' : ''}>
                    <FaQuestionCircle /> Questões do Onboarding
                </button>
                <button onClick={() => setActiveTab('visuals')} className={activeTab === 'visuals' ? 'active' : ''}>
                    <FaPalette /> Identidade Visual
                </button>
                {/* ############################################# */}
                {/* ## INÍCIO DA FUNCIONALIDADE ADICIONADA ## */}
                {/* ############################################# */}
                <button onClick={() => setActiveTab('notifications')} className={activeTab === 'notifications' ? 'active' : ''}>
                    <FaEnvelope /> Notificações
                </button>
                {/* ############################################# */}
                {/* ## FIM DA FUNCIONALIDADE ADICIONADA ## */}
                {/* ############################################# */}
            </div>

            <div className="config-content">
                {activeTab === 'questions' && (
                    <div className="tab-pane">
                        <h2>Editor de Páginas e Perguntas</h2>
                        <p>Crie páginas para organizar as perguntas do onboarding. Arraste para reordenar.</p>
                        <div className="pages-list">
                            {pages.map((page, pageIndex) => (
                                <div key={page.id} className="page-container">
                                    <div className="page-header">
                                        <input
                                            type="text"
                                            value={page.title}
                                            onChange={(e) => updatePageTitle(pageIndex, e.target.value)}
                                            className="page-title-input"
                                        />
                                        <div className="page-actions">
                                            <button onClick={() => movePage(pageIndex, pageIndex - 1)} disabled={pageIndex === 0} title="Mover para Cima"><FaArrowUp /></button>
                                            <button onClick={() => movePage(pageIndex, pageIndex + 1)} disabled={pageIndex === pages.length - 1} title="Mover para Baixo"><FaArrowDown /></button>
                                            <button onClick={() => deletePage(pageIndex)} className="delete-page-btn" title="Excluir Página"><FaTrash /></button>
                                        </div>
                                    </div>
                                    <div className="questions-list">
                                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                            <SortableContext 
                                                items={page.questions}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {page.questions.length > 0 ? (
                                                    page.questions.map((q) => (
                                                        <SortableQuestion key={q.id} question={q} pageIndex={pageIndex} onEdit={openModalForEdit} onDelete={handleDeleteQuestion} />
                                                    ))
                                                ) : (
                                                    <p className="no-questions">Nenhuma pergunta nesta página.</p>
                                                )}
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                    <button onClick={() => openModalForNew(pageIndex)} className="add-question-btn-inline">
                                        <FaPlus /> Adicionar Pergunta
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button onClick={addPage} className="add-page-btn">
                            <FaFileAlt /> Adicionar Nova Página
                        </button>
                    </div>
                )}
                
                {activeTab === 'visuals' && (
                    <div className="tab-pane">
                        <h2>Identidade Visual (White-Label)</h2>
                        <p>Faça o upload do logo e escolha as cores da sua marca.</p>
                        <div className="visuals-form">
                            <div className="form-group">
                                <label htmlFor="logoUrl">Logo da Empresa</label>
                                <input type="file" id="logoUrl" onChange={handleLogoChange} accept="image/png, image/jpeg, image/svg+xml" />
                                {(visuals.logoUrl || newLogoFile) && (
                                    <div className="logo-preview">
                                        <p>Pré-visualização:</p>
                                        <img src={newLogoFile ? URL.createObjectURL(newLogoFile) : visuals.logoUrl} alt="Logo Preview" />
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label htmlFor="primaryColor">Cor Primária</label>
                                <input type="color" id="primaryColor" name="primaryColor" value={visuals.primaryColor} onChange={handleVisualChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="secondaryColor">Cor Secundária</label>
                                <input type="color" id="secondaryColor" name="secondaryColor" value={visuals.secondaryColor} onChange={handleVisualChange} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ############################################# */}
                {/* ## INÍCIO DA FUNCIONALIDADE ADICIONADA ## */}
                {/* ############################################# */}
                {activeTab === 'notifications' && (
                    <div className="tab-pane">
                         <h2>Modelos de Notificação</h2>
                        <p>Personalize os e-mails automáticos enviados pela plataforma, como o de boas-vindas e o de liberação de resultados.</p>
                        <div className="card-actions">
                            <Link to="/configuracoes-people/notificacoes" className="btn btn-secondary">
                                Editar Templates de Notificação
                            </Link>
                        </div>
                    </div>
                )}
                {/* ############################################# */}
                {/* ## FIM DA FUNCIONALIDADE ADICIONADA ## */}
                {/* ############################################# */}

            </div>
            <footer className="config-footer">
                <button onClick={handleSaveSettings} disabled={isSaving} className="save-all-btn">
                    <FaSave /> {isSaving ? 'Salvando...' : 'Salvar Todas as Alterações'}
                </button>
            </footer>

            {isModalOpen && currentQuestion && (
                <div className="modal-overlayConfig">
                    <div className="modal-contentConfig">
                        <h2>{editingInfo.questionId ? 'Editar Pergunta' : 'Nova Pergunta'}</h2>
                        <div className="form-group">
                            <label>Nome do Campo (Label):</label>
                            <input type="text" name="label" value={currentQuestion.label} onChange={handleQuestionChange} placeholder="Ex: Qual seu CPF?"/>
                        </div>
                        <div className="form-group">
                            <label>Tipo de Resposta:</label>
                            <select name="type" value={currentQuestion.type} onChange={handleQuestionChange}>
                                <optgroup label="Campos Comuns">
                                    <option value="text">Texto Curto</option>
                                    <option value="textarea">Texto Longo</option>
                                    <option value="number">Número</option>
                                    <option value="date">Data</option>
                                    <option value="file">Anexo (Arquivo)</option>
                                    <option value="select">Seleção (Dropdown)</option>
                                </optgroup>
                                <optgroup label="Campos Inteligentes">
                                    <option value="address">Endereço Completo (CEP)</option>
                                    <option value="bankAccount">Dados Bancários</option>
                                </optgroup>
                            </select>
                        </div>
                        {currentQuestion.type === 'select' && (
                            <div className="form-group">
                                <label>Opções (separadas por vírgula):</label>
                                <input type="text" name="options" value={currentQuestion.options || ''} onChange={handleQuestionChange} placeholder="Opção 1,Opção 2,Opção 3"/>
                            </div>
                        )}
                        <div className="form-group checkbox-group">
                            <input type="checkbox" id="required" name="required" checked={currentQuestion.required} onChange={handleQuestionChange} />
                            <label htmlFor="required">Campo Obrigatório</label>
                        </div>
                        <div className="modal-actionsConfig">
                            <button onClick={() => setIsModalOpen(false)} className="btn-cancel">Cancelar</button>
                            <button onClick={handleSaveQuestion} className="btn-save">Salvar Pergunta</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfiguracoesPeople;